// Time Clock Setup — one-shot admin page to wire stations, PINs,
// and manager assignments. Saves you from bouncing between
// /admin-mod/employees, /admin/pins, and DB scripts.
//
// Workflow:
//   1. See all current stations and the employees assigned to each
//   2. + Add Station to spin up a new shared terminal account
//   3. + Add Employee under a station: name + PIN + manager picker
//   4. Change manager inline per row

import { useEffect, useState } from 'react';
import { api } from '../../api';
import Modal from '../../components/Modal';

export default function TimeClockSetupPage() {
  const [stations, setStations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [employeesByStation, setEmployeesByStation] = useState({});
  const [err, setErr] = useState('');
  const [addingStation, setAddingStation] = useState(false);
  const [addingEmployeeFor, setAddingEmployeeFor] = useState(null);

  async function load() {
    setErr('');
    try {
      const [ss, mgrs] = await Promise.all([api.listStations(), api.listManagers().catch(() => [])]);
      setStations(ss);
      setManagers(mgrs || []);
      const map = {};
      for (const s of ss) {
        map[s.id] = await api.listPinsForStation(s.id);
      }
      setEmployeesByStation(map);
    } catch (e) { setErr(e.message); }
  }
  useEffect(() => { load(); }, []);

  async function setManager(userId, managerUserId) {
    try { await api.setUserManager(userId, managerUserId || null); load(); }
    catch (e) { setErr(e.message); }
  }

  async function moveToStation(pinId, newStationId) {
    try { await api.updatePin(pinId, { home_station_user_id: newStationId }); load(); }
    catch (e) { setErr(e.message); }
  }

  async function disablePin(pinId, fullName) {
    if (!window.confirm(`Disable ${fullName}'s PIN? They won't be able to punch in until you re-enable.`)) return;
    try { await api.updatePin(pinId, { is_active: 0 }); load(); }
    catch (e) { setErr(e.message); }
  }
  async function enablePin(pinId) {
    try { await api.updatePin(pinId, { is_active: 1 }); load(); }
    catch (e) { setErr(e.message); }
  }
  async function resetPin(pinId, fullName) {
    const pin = window.prompt(`New PIN for ${fullName} (4–6 digits):`);
    if (!pin) return;
    if (!/^\d{4,6}$/.test(pin)) { alert('PIN must be 4–6 digits'); return; }
    try { await api.resetPin(pinId, pin); load(); }
    catch (e) { setErr(e.message); }
  }

  return (
    <div className="page">
      <div className="page-header">
        <h2>Time Clock Setup</h2>
        <button className="btn btn-gold" onClick={() => setAddingStation(true)}>+ Add Station</button>
      </div>
      {err && <div className="error-banner">{err}</div>}

      <div className="muted" style={{ marginBottom: 16 }}>
        Stations are shared terminals (one login). Employees PIN in under a station. Manager assignment routes payroll approvals.
      </div>

      {stations.length === 0 && <div className="empty-state">No stations yet. Click "+ Add Station" to create one (e.g. "Fort Worth Dispatch").</div>}

      {stations.map(s => (
        <div key={s.id} className="time-cards-group">
          <div className="time-cards-employee">
            <h4>
              {s.full_name}
              {s.station_region && <span className="muted"> · {s.station_region}</span>}
              <span className="muted"> · username: <code>{s.username}</code></span>
            </h4>
            <button className="btn-secondary" onClick={() => setAddingEmployeeFor(s)}>+ Add Employee</button>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Name</th><th>Manager</th><th>Station</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {(employeesByStation[s.id] || []).map(e => (
                <tr key={e.id}>
                  <td>{e.full_name}</td>
                  <td>
                    <select value={e.manager_user_id || ''} onChange={ev => setManager(e.user_id, parseInt(ev.target.value, 10) || null)}>
                      <option value="">— Pick manager —</option>
                      {managers.map(m => (
                        <option key={m.id} value={m.id}>{m.full_name}{m.position_name ? ` (${m.position_name})` : ''}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select value={e.home_station_user_id} onChange={ev => moveToStation(e.id, parseInt(ev.target.value, 10))}>
                      {stations.map(st => (
                        <option key={st.id} value={st.id}>{st.full_name}</option>
                      ))}
                    </select>
                  </td>
                  <td>{e.is_active ? <span className="status-pill status-approved">Active</span> : <span className="status-pill status-rejected">Disabled</span>}</td>
                  <td className="setup-actions">
                    <button className="btn-link" onClick={() => resetPin(e.id, e.full_name)}>Reset PIN</button>
                    {e.is_active
                      ? <button className="btn-link btn-link-danger" onClick={() => disablePin(e.id, e.full_name)}>Disable</button>
                      : <button className="btn-link" onClick={() => enablePin(e.id)}>Enable</button>
                    }
                  </td>
                </tr>
              ))}
              {(employeesByStation[s.id] || []).length === 0 && (
                <tr><td colSpan="5" className="empty">No employees yet. Click "+ Add Employee" above.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      ))}

      {addingStation && (
        <AddStationModal onClose={() => setAddingStation(false)} onDone={() => { setAddingStation(false); load(); }} />
      )}
      {addingEmployeeFor && (
        <AddEmployeeModal
          station={addingEmployeeFor}
          managers={managers}
          onClose={() => setAddingEmployeeFor(null)}
          onDone={() => { setAddingEmployeeFor(null); load(); }}
        />
      )}
    </div>
  );
}

function AddStationModal({ onClose, onDone }) {
  const [form, setForm] = useState({ full_name: '', username: '', password: '', station_region: '', phone: '' });
  const [err, setErr] = useState('');
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function submit() {
    setErr('');
    if (!form.full_name || !form.username || !form.password) { setErr('Name, username, password required'); return; }
    try { await api.createStation(form); onDone(); } catch (e) { setErr(e.message); }
  }
  return (
    <Modal title="Add Station" onClose={onClose}>
      {err && <div className="form-error">{err}</div>}
      <div className="setup-form">
        <label>
          <span>Station Name</span>
          <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Fort Worth Dispatch" />
        </label>
        <div className="setup-form-row setup-form-row-2">
          <label>
            <span>Username (login)</span>
            <input value={form.username} onChange={e => set('username', e.target.value)} placeholder="fw_dispatch" autoCapitalize="none" />
          </label>
          <label>
            <span>Password</span>
            <input type="password" value={form.password} onChange={e => set('password', e.target.value)} />
          </label>
        </div>
        <div className="setup-form-row setup-form-row-2">
          <label>
            <span>Station Region</span>
            <input value={form.station_region} onChange={e => set('station_region', e.target.value)} placeholder="Fort Worth" />
          </label>
          <label>
            <span>Shared Phone (optional)</span>
            <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 817-555-1234" />
          </label>
        </div>
      </div>
      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-gold" onClick={submit}>Create Station</button>
      </div>
    </Modal>
  );
}

function AddEmployeeModal({ station, managers, onClose, onDone }) {
  const [form, setForm] = useState({ full_name: '', pin: '', manager_user_id: '', position_name: 'Dispatcher', phone: '' });
  const [err, setErr] = useState('');
  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }
  async function submit() {
    setErr('');
    if (!form.full_name || !form.pin) { setErr('Name and PIN required'); return; }
    if (!/^\d{4,6}$/.test(form.pin)) { setErr('PIN must be 4-6 digits'); return; }
    try {
      await api.createEmployeeWithPin({
        full_name: form.full_name,
        home_station_user_id: station.id,
        pin: form.pin,
        manager_user_id: form.manager_user_id ? parseInt(form.manager_user_id, 10) : null,
        position_name: form.position_name,
        phone: form.phone || null,
      });
      onDone();
    } catch (e) { setErr(e.message); }
  }
  return (
    <Modal title={`Add Employee — ${station.full_name}`} onClose={onClose}>
      {err && <div className="form-error">{err}</div>}
      <div className="setup-form">
        <div className="setup-form-row setup-form-row-2">
          <label>
            <span>Full Name</span>
            <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Maria Lopez" />
          </label>
          <label>
            <span>PIN (4–6 digits)</span>
            <input type="text" inputMode="numeric" maxLength="6" value={form.pin}
              onChange={e => set('pin', e.target.value.replace(/\D/g, ''))} placeholder="0000" />
          </label>
        </div>
        <div className="setup-form-row setup-form-row-2">
          <label>
            <span>Position</span>
            <select value={form.position_name} onChange={e => set('position_name', e.target.value)}>
              <option value="Dispatcher">Dispatcher</option>
              <option value="Sign Technician">Sign Technician</option>
              <option value="Driver">Driver</option>
              <option value="Sales">Sales</option>
              <option value="Sales Admin">Sales Admin</option>
              <option value="Truck Maintenance">Truck Maintenance</option>
              <option value="Manager">Manager</option>
            </select>
          </label>
          <label>
            <span>Manager (reports to)</span>
            <select value={form.manager_user_id} onChange={e => set('manager_user_id', e.target.value)}>
              <option value="">— None —</option>
              {managers.map(m => (
                <option key={m.id} value={m.id}>
                  {m.full_name}{m.position_name ? ` (${m.position_name})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label>
          <span>Phone (optional, for SMS notifications)</span>
          <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+1 817-555-1234" />
        </label>
      </div>
      <div className="form-actions">
        <button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-gold" onClick={submit}>Add Employee</button>
      </div>
    </Modal>
  );
}
